<template>
  <DetailContentsLayout :is-mobile="isMobile" :loading="loading">
    <template v-slot:header>
      <PageBack background="primary" back-color="white" @back="$emit('back')" />
    </template>

    <template v-slot:card>
      <LeaseCardBasic
        :width="isMobile ? undefined : '360px'"
        :img="lease.vehicleImg"
        :rego="lease.vehicleRego"
        :term="lease.term"
        :current-month="lease.currentMonth"
      />
    </template>

    <template v-slot:details>
      <h4 class="text-weight-medium text-grey-10 q-mt-lg">Lease Details</h4>
      <QSeparator class="q-my-md" color="grey-4" />

      <div class="row items-end q-mb-md">
        <div class="col">
          <p class="text-grey-6 q-mb-xs">Lease type</p>
          <p class="text-grey-10">{{ lease.leaseType }}</p>
        </div>
        <ActiveTag :active="lease.leaseActive" />
      </div>

      <SAITableSummary :list="savings" title="Your Monthly Novated Lease" />
      <QSeparator color="primary" />
      <div class="q-pa-md row items-start">
        <div class="col">
          <p class="text-weight-medium q-mb-xs">Total Lease Cost</p>
          <span>(ex GST)</span>
        </div>
        <p class="text-weight-medium">
          {{ lease.totalLeaseCost | currency }}
        </p>
      </div>

      <div class="q-pa-md bg-primary-pale row" v-if="lease.impactOnTakeHomePay">
        <p class="col-6">Estimated net impact on your take home pay</p>
        <div class="col-6 text-right">
          <p class="text-h2 text-primary">
            {{ lease.impactOnTakeHomePay | currency }}
          </p>
          <span class="text-grey-6">/ month</span>
        </div>
      </div>

      <div class="row q-mb-md q-mt-lg">
        <div class="col-6" v-if="lease.leaseStartDate">
          <p class="text-grey-6 q-mb-xs">Lease Start Date</p>
          <p class="text-grey-10">
            {{ lease.leaseStartDate | parseDate(undefined, 'dd MMM yyyy') }}
          </p>
        </div>
        <div class="col-6" v-if="lease.leaseEndDate">
          <p class="text-grey-6 q-mb-xs">Lease End Date</p>
          <p class="text-grey-10">
            {{ lease.leaseEndDate | parseDate(undefined, 'dd MMM yyyy') }}
          </p>
        </div>
      </div>
      <div class="row q-mb-md">
        <div class="col-6">
          <p class="text-grey-6 q-mb-xs">Lease Term</p>
          <LeaseTermCompletion
            :current-month="lease.currentMonth"
            :term="lease.term"
            term-color="grey-10"
          />
        </div>
        <div class="col-6">
          <p class="text-grey-6 q-mb-xs">KM Annual</p>
          <p class="text-grey-10">
            {{ lease.leaseAnnualKM }}
          </p>
        </div>
      </div>
      <div class="row q-mb-lg" v-if="lease.interestRate || lease.financier">
        <div class="col-6" v-if="lease.interestRate">
          <p class="text-grey-6 q-mb-xs">Interest Rate</p>
          <p class="text-grey-10">{{ lease.interestRate }}%</p>
        </div>
        <div class="col-6" v-if="lease.financier">
          <p class="text-grey-6 q-mb-xs">Financier</p>
          <p class="text-grey-10">
            {{ lease.financier }}
          </p>
        </div>
      </div>

      <div
        class="q-pa-md q-mb-sm bg-primary-pale rounded-borders"
        v-if="lease.residualValue"
      >
        <span class="text-paragraph">Your residual is</span>
        <span class="text-weight-bold text-primary text-paragraph">
          {{ lease.residualValue | currency }}
        </span>
        <span class="text-paragraph">at end of lease</span>
      </div>

      <DocumentBtn
        v-for="(document, index) in lease.primaryDocuments"
        :key="index"
        class="q-mb-sm full-width"
        :label="document.label"
        :href="document.url"
      />

      <QSeparator class="q-mt-lg q-mb-md" color="grey-4" />

      <div class="column">
        <ExpansionItem label="Vehicle Details">
          <p
            class="text-italic q-pt-sm q-pb-md text-body"
            v-if="lease.registrationPaidUntil"
          >
            Your registration is paid until
            {{ lease.registrationPaidUntil | parseDate }}
          </p>
          <div class="row q-mb-md">
            <div class="col-12">
              <p class="text-grey-6 q-mb-xs">Vehicle Make and Model</p>
              <p class="text-grey-10">{{ lease.vehicleDescription }}</p>
            </div>
          </div>
          <div class="row q-mb-sm" v-if="lease.vehicleRego || lease.vehicleVin">
            <div class="col-6" v-if="lease.vehicleRego">
              <p class="text-grey-6 q-mb-xs">Plate Number</p>
              <p class="text-grey-10">{{ lease.vehicleRego }}</p>
            </div>
            <div class="col-6" v-if="lease.vehicleVin">
              <p class="text-grey-6 q-mb-xs">Vin</p>
              <p class="text-grey-10">{{ lease.vehicleVin }}</p>
            </div>
          </div>
        </ExpansionItem>

        <QSeparator
          class="q-my-md"
          color="grey-4"
          v-if="lease.insurer || lease.insuranceRenewalDate"
        />

        <ExpansionItem
          label="Comprehensive Insurance"
          v-if="lease.insurer || lease.insuranceRenewalDate"
        >
          <div class="row q-my-md">
            <div class="col-6" v-if="lease.insurer">
              <p class="text-grey-6 q-mb-xs">Insurer</p>
              <p class="text-grey-10">{{ lease.insurer }}</p>
            </div>
            <div class="col-6" v-if="lease.insuranceRenewalDate">
              <p class="text-grey-6 q-mb-xs">Renewal Date</p>
              <p class="text-grey-10">
                {{ lease.insuranceRenewalDate | parseDate }}
              </p>
            </div>
          </div>
          <DocumentBtn
            v-for="(document, index) in lease.allianzDocuments"
            :key="index"
            class="q-mb-sm full-width"
            :label="document.label"
            :href="document.url"
          />
        </ExpansionItem>

        <QSeparator
          class="q-my-md"
          color="grey-4"
          v-if="lease.otherDocuments && lease.otherDocuments.length"
        />

        <ExpansionItem
          label="Other Related Information"
          v-if="lease.otherDocuments && lease.otherDocuments.length"
        >
          <DocumentBtn
            v-for="(document, index) in lease.otherDocuments"
            :key="index"
            class="q-mt-md full-width"
            :label="document.label"
            :href="document.url"
          />
        </ExpansionItem>
      </div>
    </template>
  </DetailContentsLayout>
</template>

<script>
import { QSeparator } from 'quasar';
import { SAITableSummary } from 'ecx.saigon.ui.common';

export default {
  name: 'LeaseDetail',
  props: {
    isMobile: Boolean,
    loading: Boolean,
    lease: {
      type: Object,
      default: () => ({})
    },
    savings: {
      type: Array,
      default: () => []
    }
  },
  components: {
    DetailContentsLayout: () => import('../../layouts/detail-contents-layout'),
    QSeparator,
    SAITableSummary,
    LeaseTermCompletion: () => import('../../molecule/lease-term-completion'),
    ExpansionItem: () => import('../../atom/expansion-item'),
    ActiveTag: () => import('../../atom/active-tag'),
    PageBack: () => import('../../molecule/page-back'),
    LeaseCardBasic: () => import('../../molecule/lease-card-basic'),
    DocumentBtn: () => import('../../atom/document-btn')
  }
};
</script>
